import type { MouseEvent } from 'react';

import { useColorModeValue } from '@ecoinvent/ui';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath } from '@xyflow/react';

import { COLORS, DIM_OPACITY, FUNCTIONAL_ACCENT_DARK, FUNCTIONAL_ACCENT_LIGHT } from './constants';

// All optional: the plain overflow edges carry no data at all.
export type SupplyChainEdgeData = {
  strokeWidth?: number;
  edgeVariant?: 'input' | 'consumer';
  // Orthogonal to edgeVariant: the functional flow can sit on either side, and only recolours the line.
  isFunctional?: boolean;
  mode?: 'flows' | 'impact_assessment';
  dimmed?: boolean;
  hovered?: boolean;
  onHover?: (id: string, hovered: boolean) => void;
  onDeselect?: (event: MouseEvent) => void;
};

const SupplyChainEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  label,
}: EdgeProps) => {
  const edgeData = data as SupplyChainEdgeData | undefined;
  const isDimmed = edgeData?.dimmed ?? false;
  const isHovered = edgeData?.hovered ?? false;
  const strokeWidth = edgeData?.strokeWidth ?? 2;
  const isConsumer = edgeData?.edgeVariant === 'consumer';
  const isFlowsMode = edgeData?.mode === 'flows';
  // Impact assessment only: flows mode keeps its uniform grey lines.
  const isFunctional = (edgeData?.isFunctional ?? false) && edgeData?.mode === 'impact_assessment';

  const inputEdgeColor = useColorModeValue(COLORS.greenLight, COLORS.greenDark);
  const consumerEdgeColor = useColorModeValue(COLORS.purpleLight, COLORS.purpleDark);
  const flowsEdgeColor = useColorModeValue(COLORS.grayDim, COLORS.grayDark);
  const hoverInputEdgeColor = useColorModeValue(COLORS.greenLight, COLORS.greenDark);
  const hoverConsumerEdgeColor = useColorModeValue(COLORS.hoverConsumerLineLight, COLORS.hoverConsumerLineDark);
  const functionalEdgeColor = useColorModeValue(FUNCTIONAL_ACCENT_LIGHT, FUNCTIONAL_ACCENT_DARK);

  const normalColor = isFlowsMode
    ? flowsEdgeColor
    : isFunctional
      ? functionalEdgeColor
      : isConsumer
        ? consumerEdgeColor
        : inputEdgeColor;
  // Functional edges keep their accent on hover; the stroke-opacity lift below is their hover feedback.
  const hoverColor = isFunctional ? functionalEdgeColor : isConsumer ? hoverConsumerEdgeColor : hoverInputEdgeColor;
  const edgeColor = isHovered ? hoverColor : normalColor;

  const labelBg = useColorModeValue(COLORS.borderLight, COLORS.grayDark);
  const labelColor = useColorModeValue(COLORS.textLight, '#FFFFFF');
  const hoverInputLabelBg = useColorModeValue(COLORS.hoverLabelBgLight, COLORS.hoverLabelBgDark);
  const hoverInputLabelColor = useColorModeValue(COLORS.hoverLabelColorLight, COLORS.hoverLabelColorDark);
  const hoverConsumerLabelBg = useColorModeValue(COLORS.hoverConsumerLabelBgLight, COLORS.hoverConsumerLabelBgDark);
  const hoverConsumerLabelColor = useColorModeValue(COLORS.hoverConsumerLabelColorLight, COLORS.hoverConsumerLabelColorDark);
  // Functional edges keep the resting chip on hover too — there is no functional label token.
  const hoverLabelBg = isFunctional ? labelBg : isConsumer ? hoverConsumerLabelBg : hoverInputLabelBg;
  const hoverLabelColor = isFunctional ? labelColor : isConsumer ? hoverConsumerLabelColor : hoverInputLabelColor;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const onMouseEnter = () => edgeData?.onHover?.(id, true);
  const onMouseLeave = () => edgeData?.onHover?.(id, false);
  // The line and its label are hit targets now, so they have to keep offering the pane's deselect gesture.
  const onClick = (event: MouseEvent) => edgeData?.onDeselect?.(event);

  return (
    <>
      <g style={{ opacity: isDimmed ? DIM_OPACITY : 1, transition: 'opacity 0.2s' }}>
        <BaseEdge
          id={id}
          path={edgePath}
          style={{
            stroke: edgeColor,
            strokeWidth,
            strokeOpacity: isHovered ? 1 : 0.65,
            transition: 'stroke 0.15s, stroke-opacity 0.15s',
          }}
        />
        {/* Invisible wider path for hover detection on the edge line itself */}
        <path
          d={edgePath}
          fill="none"
          stroke="transparent"
          strokeWidth={16}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          // Required: React Flow marks non-selectable, non-clickable edges `.inactive`, which sets
          // pointer-events:none on the whole group and would swallow this path's hover events.
          style={{ cursor: 'default', pointerEvents: 'all' }}
        />
      </g>
      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              // 0 paints the label above the edge lines but below the cards, so the expand pills stay clickable.
              zIndex: 0,
              fontSize: 11,
              color: isHovered ? hoverLabelColor : labelColor,
              background: isHovered ? hoverLabelBg : labelBg,
              padding: '2px 6px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              cursor: 'default',
              opacity: isDimmed ? DIM_OPACITY : 1,
              transition: 'background 0.15s, color 0.15s, opacity 0.2s',
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default SupplyChainEdge;
